import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faHome, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';

// props

// components
import { ActionButton, CommandBar, ICommandBarItemProps, PrimaryButton, Stack } from '@fluentui/react';
import Text from '../../../../typography/text';
import LoadingComponent from '../../../../feedbacks/loading';
import Label from '../../../../typography/label';
import DetailsStack, { ISectionTabProps, IStackSectionProps } from '../../../../uiframeworks/detailsStack';
import SubsidiariesForm from '../form';
import ISubsidiariesResourceShort from '../../../../../props/data/subsidiaries';
import SubsidiariesService from '../../../../../services/data/subsidiaries';
import DeleteSubsidiariesForm from '../delete';
import { NavLink } from 'react-router-dom';
import OrdersList from '../../../../orders/general/list';
import InternalBankAccountsList from '../../../../data/bankaccounts/internal/list';
import BankAccountsList from '../../../../bankaccounts/general/list';
import Details from '../../../../typography/details';
import SubsidiaryDocumentsList from './documents';
import DocumentService from '../../../../../services/documents/document';
import FileService from '../../../../../services/general/file';

type SubsidiaryDetailsProps = {
    subsidiaryId: string;
}

const SubsidiaryDetails: React.FC<SubsidiaryDetailsProps> = (props: SubsidiaryDetailsProps) => {
    const { banner, topbar } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [subsidiary, setSubsidiary] = React.useState<ISubsidiariesResourceShort>();
    const [logo, setLogo] = React.useState<string[] | undefined>();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    useEffect(() => {
        _onGetSubsidiary();
    }, []);

    const getLogoBase64 = async (path: string) => {
        const base64 = (await DocumentService.get(encodeURIComponent(path))).data.data;
        const images = await FileService.getImageBase64(base64, path.split(".").pop() || "");
        setLogo(images);
    }

    const _onGetSubsidiary = async () => {
        try {
            setLoaded(false);
            const _subsidiary = await SubsidiariesService.get(props.subsidiaryId);
            setSubsidiary(_subsidiary);

            if (_subsidiary.logo) {
                getLogoBase64(_subsidiary.logo.path);
            }

            topbar.show(_subsidiary.name, [
                { key: "home", icon: faHome, href: "/" },
                { key: "subsidiaries", text: 'Subsidiaries', href: "/settings/subsidiaries" },
                { key: props.subsidiaryId, text: _subsidiary.name }
            ]);

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_subsidiary_error',
                text: 'Failed to get subsidiary details. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        items.push({
            key: 'update',
            text: 'Update details',
            onRender: () => <PrimaryButton text={'Update Details'} onClick={() => setActiveSurface('update')} />
        });

        farItems.push({
            key: 'delete',
            text: 'Delete',
            onRender: () => <ActionButton text={'Delete'} iconProps={{ iconName: "Delete" }} styles={{ root: { marginLeft: 20 } }} onClick={() => setActiveSurface('delete')} />
        });

        return { items, farItems };
    }

    const getLogoSection = (_subsidiary: ISubsidiariesResourceShort): IStackSectionProps => {
        return {
            header: {
                title: "Logo"
            },
            key: 'subsidiary_logo',
            isCard: true,
            content: <Stack tokens={{ childrenGap: 20 }}>
                {logo && _subsidiary.logo ? logo.map((preview) => {
                    return <img src={preview} />;
                }) : null}
                {!logo && _subsidiary.logo ? <LoadingComponent label={'Retrieving logo ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                {!_subsidiary.logo ? <Text size={'small'} style={{fontStyle: 'italic'}}>No logo found</Text> : null}
            </Stack>
        }
    }

    const getSubsidiariesPropertiesSection = (_subsidiary: ISubsidiariesResourceShort): IStackSectionProps => {
        return {
            header: {
                title: "Details"
            },
            key: 'subsidiary_details',
            isCard: true,
            content: <Stack tokens={{ childrenGap: 20 }}>
                <Details title={"Name"} text={_subsidiary.name} />
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Details title={"Phone number"} text={_subsidiary.phoneNumber || "-"} style={{width: '50%'}} />
                    <Details title={"Address"} text={_subsidiary.address || "-"} style={{width: '50%'}} />
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Details title={"Legal name"} text={_subsidiary.legalName || "-"} style={{width: '50%'}} />
                    <Details title={"NPWP"} text={_subsidiary.npwp || "-"} style={{width: '50%'}} />
                </Stack>
            </Stack>
        }
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        if (refresh) { _onGetSubsidiary() }
    }

    const getRelatedItemsSection = (_subsidiary: ISubsidiariesResourceShort): IStackSectionProps => {
        const qs: string[] = [];
        qs.push(`subsidiary=${_subsidiary.id}`);

        const tabs: ISectionTabProps[] = [];
        tabs.push({
            key: 'related_orders',
            title: 'Related Orders',
            content: <Stack>
                <OrdersList qs={qs} hideCreateOrder />
            </Stack>
        });

        tabs.push({
            key: 'bank_accounts',
            title: 'Bank Accounts',
            content: <Stack>
                <BankAccountsList subsidiaryId={_subsidiary.id} />
            </Stack>
        });

        tabs.push({
            key: 'documents',
            title: 'Documents',
            content: <Stack>
                <SubsidiaryDocumentsList subsidiaryId={_subsidiary.id} />
            </Stack>
        });

        const section: IStackSectionProps = {
            key: 'subsidiary_details',
            isCard: true,
            tabs
        }

        return section;
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!loaded ? <LoadingComponent label='Retrieving subsidiary details ...' labelPosition='right' spinnerPosition='baseline' /> : null}
        {loaded && subsidiary ? <>
            <CommandBar
                items={getCommandBarItems().items}
                farItems={getCommandBarItems().farItems}
                styles={{
                    root: {
                        padding: 0,
                        height: 'unset',
                        backgroundColor: 'transparent'
                    }
                }}
                ariaLabel="Use left and right arrow keys to navigate between commands" />
            <Stack className={styles.details} tokens={{ childrenGap: 20 }}>
                <DetailsStack left={[getSubsidiariesPropertiesSection(subsidiary)]} right={[getLogoSection(subsidiary)]} />
                <DetailsStack left={[getRelatedItemsSection(subsidiary)]} />
            </Stack>
            {activeSurface === 'update' ? <SubsidiariesForm subsidiariesId={subsidiary.id} onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'delete' ? <DeleteSubsidiariesForm subsidiaries={subsidiary} onDismissed={_onSurfaceDismissed} /> : null}
        </> : null}
    </Stack>;
};

export default SubsidiaryDetails;
